import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Avatar,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  SmartToy as ChatbotIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { chatbotService } from '../../services/chatbot';

interface ChatWidgetProps {
  open: boolean;
  onClose: () => void;
  onNewMessage?: () => void;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ open, onClose, onNewMessage }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'bot'; content: string; timestamp: Date }>>([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: history = [] } = useQuery({
    queryKey: ['chatHistory'],
    queryFn: chatbotService.getHistory,
    enabled: open,
  });

  useEffect(() => {
    if (history.length > 0 && messages.length === 0) {
      const historyMessages = history.slice(-10).map((h) => [
        { role: 'user' as const, content: h.user_message, timestamp: new Date(h.created_at) },
        { role: 'bot' as const, content: h.bot_response, timestamp: new Date(h.created_at) },
      ]).flat();
      setMessages(historyMessages);
    }
  }, [history, open]);

  useEffect(() => {
    if (open && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open, isMinimized]);

  const sendMutation = useMutation({
    mutationFn: chatbotService.sendMessage,
    onSuccess: (data) => {
      const botResponse = data.response || 'I apologize, but I encountered an error.';
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: message, timestamp: new Date() },
        { role: 'bot', content: botResponse, timestamp: new Date() },
      ]);
      setMessage('');
      // Notify parent about new message if chat is closed or minimized
      // Use setTimeout to check state after update
      setTimeout(() => {
        if ((!open || isMinimized) && onNewMessage) {
          onNewMessage();
        }
      }, 100);
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: message, timestamp: new Date() },
        { role: 'bot', content: 'I apologize, but I encountered an error. Please try again.', timestamp: new Date() },
      ]);
      setMessage('');
    },
  });

  const handleSend = () => {
    if (message.trim() && !sendMutation.isPending) {
      sendMutation.mutate(message.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickPrompts = [
    "I'm hungry",
    "Best card for groceries",
    "Plan a vacation",
    "Where should I shop?",
    "Show me dining offers",
  ];

  const drawerWidth = 380;
  const drawerHeight = 600;
  const buttonBottom = 24;
  const buttonRight = 24;
  const buttonSize = 64;
  const spacing = 16; // Space between button and chat widget

  return (
    <>
      <Drawer
        anchor="right"
        open={open && !isMinimized}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: drawerWidth,
            height: drawerHeight,
            maxHeight: '90vh',
            position: 'fixed',
            bottom: buttonBottom + buttonSize + spacing, // Position right above the button
            right: buttonRight,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            // Smooth animation when opening (chat bubble effect)
            animation: open && !isMinimized ? 'chatBubbleOpen 0.3s ease-out' : 'none',
            '@keyframes chatBubbleOpen': {
              '0%': {
                opacity: 0,
                transform: 'scale(0.8) translateY(20px)',
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1) translateY(0)',
              },
            },
            // Add a small triangle/pointer pointing to the button (chat bubble effect)
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -12,
              right: 20,
              width: 0,
              height: 0,
              borderLeft: '12px solid transparent',
              borderRight: '12px solid transparent',
              borderTop: '12px solid white',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
            },
          },
        }}
        ModalProps={{
          keepMounted: true,
          style: { zIndex: 1300 },
        }}
        hideBackdrop={false}
        variant="temporary"
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
              <ChatbotIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                AI Assistant
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                We're here to help
              </Typography>
            </Box>
          </Box>
          <Box>
            <IconButton
              size="small"
              onClick={() => {
                setIsMinimized(true);
                // Notify parent when minimized (so badge can show if new messages arrive)
                if (onNewMessage) {
                  // Small delay to ensure state is updated
                  setTimeout(() => {
                    if (onNewMessage) onNewMessage();
                  }, 100);
                }
              }}
              sx={{ color: 'white', mr: 0.5 }}
            >
              <MinimizeIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            bgcolor: '#f8fafc',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ChatbotIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom fontWeight={600}>
                Hi! How can I help you today?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Ask me about your cards, spending, or savings opportunities
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {quickPrompts.map((prompt, idx) => (
                  <Chip
                    key={idx}
                    label={prompt}
                    onClick={() => setMessage(prompt)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'white',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {messages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  gap: 1,
                  maxWidth: '80%',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                    width: 32,
                    height: 32,
                    fontSize: 16,
                  }}
                >
                  {msg.role === 'user' ? <PersonIcon sx={{ fontSize: 18 }} /> : <ChatbotIcon sx={{ fontSize: 18 }} />}
                </Avatar>
                <Paper
                  sx={{
                    p: 1.5,
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                    color: msg.role === 'user' ? 'white' : 'text.primary',
                    borderRadius: 2,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {msg.content}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          ))}

          {sendMutation.isPending && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                  <ChatbotIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Paper sx={{ p: 1.5, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  <CircularProgress size={16} />
                </Paper>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Input Area */}
        <Box sx={{ p: 2, bgcolor: 'white' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sendMutation.isPending}
              size="small"
              multiline
              maxRows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={!message.trim() || sendMutation.isPending}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                minWidth: 48,
                height: 40,
                borderRadius: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #db2777 100%)',
                },
              }}
            >
              {sendMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: 20 }} />
              )}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Minimized State */}
      {open && isMinimized && (
        <Paper
          onClick={() => setIsMinimized(false)}
          sx={{
            position: 'fixed',
            bottom: buttonBottom + buttonSize + spacing, // Same position as expanded chat
            right: buttonRight,
            width: 300,
            p: 2,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            color: 'white',
            zIndex: 1300,
            '&:hover': {
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
            },
            transition: 'all 0.3s ease',
            // Add a small triangle/pointer pointing to the button
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -12,
              right: 20,
              width: 0,
              height: 0,
              borderLeft: '12px solid transparent',
              borderRight: '12px solid transparent',
              borderTop: '12px solid',
              borderTopColor: '#6366f1', // Match gradient start color
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                <ChatbotIcon />
              </Avatar>
              {messages.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: '#ef4444',
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                AI Assistant
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {messages.length > 0 ? `${messages.length} message${messages.length > 1 ? 's' : ''}` : 'Click to open chat'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ChatWidget;
